var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { s as sectionRenderer, t as lazyReveal, q as activateInteractionAssets } from "./utilities-Da0sjanJ.js";
const STAGGER_DELAY_MS = 100;
const STAGGER_DELAY_SHORT_MS = 50;
const SCROLL_THRESHOLD_PX = 500;
class PaginationComponent extends HTMLElement {
  constructor() {
    super();
    // ─── Back / Forward ───────────────────────────────────────────────────
    __publicField(this, "handlePopState", (event) => {
      this.loadPageContent(window.location.href);
    });
    this.loading = false;
    this.allLoaded = false;
  }
  connectedCallback() {
    this.paginationType = this.dataset.paginationType;
    this.cacheNodes();
    this._attachListeners();
  }
  // ─── Event binding helpers ─────────────────────────────────────────────
  _attachListeners() {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener("click", () => this.handleLoadMore());
    }
    if (this.paginationType === "infinite") {
      this._initInfiniteScroll();
    } else if (this.paginationType === "number") {
      this._initNumberPaginationDelegation();
    }
    window.addEventListener("popstate", this.handlePopState);
  }
  // ─── DOM cache ────────────────────────────────────────────────────────
  cacheNodes() {
    const section = this.closest(".shopify-section") || document;
    this.paginationContentInner = section.querySelector(".pagination-content-inner");
    this.grid = section.querySelector(".pagination-container");
    this.loadMoreBtn = this.querySelector(".js-load-more");
  }
  // ─── Config helpers ───────────────────────────────────────────────────
  getConfig() {
    var _a, _b, _c, _d;
    return {
      totalPages: parseInt((_a = this.querySelector("[data-all-pages]")) == null ? void 0 : _a.value) || 0,
      currentPage: parseInt((_b = this.querySelector("[data-this-page]")) == null ? void 0 : _b.value) || 1,
      url: ((_c = this.querySelector("[data-url]")) == null ? void 0 : _c.value) || "",
      nextUrl: ((_d = this.querySelector("[data-next-link]")) == null ? void 0 : _d.value) || ""
    };
  }
  updateConfig(currentPage, nextUrl) {
    var _a, _b;
    (_a = this.querySelector("[data-next-link]")) == null ? void 0 : _a.setAttribute("value", nextUrl);
    (_b = this.querySelector("[data-this-page]")) == null ? void 0 : _b.setAttribute("value", currentPage);
  }
  // ─── Utilities ────────────────────────────────────────────────────────
  buildNextUrl(currentUrl, url, nextPage) {
    const urlObj = new URL(currentUrl, window.location.origin);
    const viewParam = urlObj.searchParams.get("view");
    return viewParam ? `${url}?page=${nextPage}&view=${viewParam}` : `${url}?page=${nextPage}`;
  }
  getTransitionParams() {
    const inner = this.paginationContentInner;
    if (!inner) return { duration: 200, easing: "ease" };
    const style = getComputedStyle(inner);
    const dur = style.getPropertyValue("--transition-duration") || "200ms";
    const ease = style.getPropertyValue("--transition-easing") || "ease";
    return {
      duration: dur.includes("ms") ? parseInt(dur) : parseFloat(dur) * 1e3,
      easing: ease
    };
  }
  // Read stagger delay from CSS variable (fallback to constant)
  _staggerDelay(short = false) {
    const varVal = getComputedStyle(this).getPropertyValue("--pagination-stagger-delay").trim();
    if (varVal) {
      return parseInt(varVal);
    }
    return short ? STAGGER_DELAY_SHORT_MS : STAGGER_DELAY_MS;
  }
  // ─── Loading states ───────────────────────────────────────────────────
  showLoadingState() {
    this.loading = true;
    if (this.loadMoreBtn) {
      const content = this.loadMoreBtn.querySelector(".btn-custom-content");
      if (content) content.setAttribute("loading", "");
      this.loadMoreBtn.disabled = true;
    }
    if (this.paginationContentInner) {
      Object.assign(this.paginationContentInner.style, {
        opacity: "0.5",
        pointerEvents: "none",
        transition: "opacity var(--trans-duration) var(--trans-easing)"
      });
    }
    if (this.grid) this.grid.setAttribute("aria-busy", "true");
  }
  hideLoadingState() {
    this.loading = false;
    if (this.loadMoreBtn) {
      const content = this.loadMoreBtn.querySelector(".btn-custom-content");
      if (content) content.removeAttribute("loading");
      this.loadMoreBtn.disabled = this.allLoaded;
    }
    if (this.paginationContentInner) {
      Object.assign(this.paginationContentInner.style, {
        opacity: "1",
        pointerEvents: ""
      });
    }
    if (this.grid) this.grid.removeAttribute("aria-busy");
  }
  // ─── Core: fetch + render ───────────────────────────────────────────
  /**
   * Fetch a URL and update DOM using sectionRenderer.
   * Does NOT touch browser history.
   */
  async loadPageContent(url) {
    var _a;
    if (this.loading) return;
    this.showLoadingState();
    try {
      const fetchUrl = new URL(url, window.location.origin);
      const sectionId = (_a = this.closest(".shopify-section")) == null ? void 0 : _a.id.replace("shopify-section-", "");
      if (!sectionId) {
        window.location.href = url;
        return;
      }
      await sectionRenderer.renderSection(sectionId, { url: fetchUrl, cache: true });
      this.cacheNodes();
      const revealScope = this.paginationContentInner || this.grid;
      if (revealScope) {
        lazyReveal(revealScope);
      }
      if (this.grid) {
        const stagger = this._staggerDelay(true);
        this.grid.querySelectorAll(":scope > *").forEach((item, i) => {
          item.classList.add("is-loading");
          setTimeout(() => item.classList.remove("is-loading"), i * stagger);
        });
      }
      this._updateActiveLink(url);
      this._scrollToListing();
    } catch (err) {
      window.location.href = url;
    } finally {
      this.hideLoadingState();
    }
  }
  // ─── Number pagination click ────────────────────────────────────────────
  async handlePageClick(event, link) {
    event.preventDefault();
    const anchor = link || event.currentTarget;
    const url = anchor.href || anchor.getAttribute("href");
    if (this.loading || !url) return;
    await this.loadPageContent(url);
    window.history.pushState({}, "", url);
  }
  // ─── Scroll helpers ────────────────────────────────────────────────────
  _scrollToListing() {
    const listing = document.querySelector(".pagination-content-inner");
    if (!listing) return;
    const targetY = listing.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: targetY, behavior: "smooth" });
  }
  // ─── a11y: mark the active page link ──────────────────────────────────
  _updateActiveLink(url) {
    this.querySelectorAll(".js-pagination-link").forEach((link) => {
      link.removeAttribute("aria-current");
    });
    const activeLink = [...this.querySelectorAll(".js-pagination-link")].find((link) => link.getAttribute("href") === url);
    if (activeLink) {
      activeLink.setAttribute("aria-current", "page");
      activeLink.setAttribute("aria-disabled", "true");
      activeLink.setAttribute("tabindex", "-1");
    }
  }
  // ─── Infinite scroll ───────────────────────────────────────────────────
  _initInfiniteScroll() {
    if (!this.grid) return;
    this.scrollHandler = () => this._handleScroll();
    window.addEventListener("scroll", this.scrollHandler);
  }
  _handleScroll() {
    if (this.loading || this.allLoaded) return;
    const threshold = document.body.offsetHeight - window.innerHeight - SCROLL_THRESHOLD_PX;
    if (window.scrollY >= threshold) {
      this.handleLoadMore();
    }
  }
  // ─── Number pagination init (delegation — set up once) ───────────────
  _initNumberPaginationDelegation() {
    this.addEventListener("click", (e) => {
      const link = e.target.closest(".js-pagination-link");
      if (!link || !this.contains(link)) return;
      this.handlePageClick(e, link);
    });
  }
  // ─── Load more (infinite / load-more) ─────────────────────────────────
  async handleLoadMore() {
    if (this.loading || this.allLoaded) return;
    const config = this.getConfig();
    if (!config.nextUrl) return;
    this.showLoadingState();
    const newPage = config.currentPage + 1;
    try {
      const { items, newNextUrl } = await this._fetchItems(config.nextUrl);
      if (items.length > 0) {
        this._appendItems(items);
        this.updateConfig(newPage, newNextUrl || "");
      }
      if (newPage >= config.totalPages || !newNextUrl) {
        this.allLoaded = true;
      }
    } catch (err) {
    } finally {
      this.hideLoadingState();
      if (this.allLoaded) {
        this.classList.add("is-hidden");
        this._teardownInfiniteScroll();
      }
    }
  }
  // ─── Helpers ──────────────────────────────────────────────────────────
  async _fetchItems(url) {
    var _a, _b, _c;
    const fetchUrl = new URL(url, window.location.origin);
    const sectionId = (_a = this.closest(".shopify-section")) == null ? void 0 : _a.id.replace("shopify-section-", "");
    let html;
    if (sectionId) {
      html = await sectionRenderer.getSectionHTML(sectionId, true, fetchUrl);
    } else {
      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      html = await response.text();
    }
    const doc = new DOMParser().parseFromString(html, "text/html");
    return {
      items: [...((_b = doc.querySelector(".pagination-container")) == null ? void 0 : _b.children) || []],
      newNextUrl: ((_c = doc.querySelector("[data-next-link]")) == null ? void 0 : _c.value) ?? null
    };
  }
  _appendItems(items) {
    if (!this.grid || items.length === 0) return;
    const stagger = this._staggerDelay();
    const newItems = [];
    items.forEach((item) => {
      if (item.classList.contains("article-page-featured-listing")) return;
      item.classList.add("is-loading");
      this.grid.insertAdjacentHTML("beforeend", item.outerHTML);
      newItems.push(this.grid.lastElementChild);
    });
    newItems.forEach((item, i) => {
      setTimeout(() => item.classList.remove("is-loading"), i * stagger);
    });
    lazyReveal(this.grid);
    activateInteractionAssets(this.grid);
    if (this.paginationType === "load-more" && newItems.length > 0) {
      const firstNewLink = newItems[0].querySelector('a[href]:not([tabindex="-1"])') || newItems[0];
      if (firstNewLink && typeof firstNewLink.focus === "function") {
        firstNewLink.focus({ preventScroll: true });
      }
    }
  }
  // ─── Teardown ─────────────────────────────────────────────────────────
  _teardownInfiniteScroll() {
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
      this.scrollHandler = null;
    }
  }
  disconnectedCallback() {
    this._teardownInfiniteScroll();
    window.removeEventListener("popstate", this.handlePopState);
  }
}
customElements.define("page-pagination-component", PaginationComponent);
